import { Loader2, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DataTable, type Column, EntityStatusPill, ProgressBar, EmptyState, Button } from "@/design/components";
import { useMoney } from "@/domain/hooks";
import type { CrossDealProject } from "@/domain/hooks";

/**
 * Shared presentational table for the Oversight/Funder cross-deal "Projects"
 * screen (StratusProjectsPage.tsx / FunderProjectsPage.tsx) - one table for
 * both personas since the columns and states are identical; only visibility
 * filtering and empty-state copy differ, and that's the caller's job.
 * Mirrors WorkspacePages.tsx's WorkspaceProjectsPage columns, plus a Deal
 * column since rows now span multiple programmes.
 */
export function CrossDealProjectsTable({
  projects,
  dealNameByWorkspaceId,
  isLoading,
  failedCount,
  onRetry,
  emptyTitle,
  emptyBody,
}: {
  projects: CrossDealProject[];
  dealNameByWorkspaceId: Record<string, string>;
  isLoading: boolean;
  failedCount: number;
  onRetry: () => void;
  emptyTitle: string;
  emptyBody: string;
}) {
  const navigate = useNavigate();
  const money = useMoney();

  if (isLoading && projects.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-mute">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  // Total failure: nothing loaded at all.
  if (!isLoading && failedCount > 0 && projects.length === 0) {
    return (
      <EmptyState
        title="Couldn't load projects"
        body="Something went wrong fetching your deals' projects."
        cta={<Button variant="secondary" onClick={onRetry}>Retry</Button>}
      />
    );
  }

  const columns: Column<CrossDealProject>[] = [
    {
      key: "name",
      header: "Project",
      cell: (p) => (
        <div>
          <p className="font-medium text-ink">{p.name}</p>
          <p className="text-xs text-faint">{p.district} · {p.region}</p>
        </div>
      ),
    },
    {
      key: "deal",
      header: "Deal",
      cell: (p) => <span className="text-mute">{dealNameByWorkspaceId[p.workspaceId] ?? "—"}</span>,
    },
    {
      key: "contractor",
      header: "Contractor",
      cell: (p) => <span className="text-mute">{p.contractorName}</span>,
    },
    {
      key: "budget",
      header: "Budgeted",
      align: "num",
      cell: (p) => <span className="text-mute">{money(p.budgeted)}</span>,
    },
    {
      key: "spent",
      header: "Spent",
      cell: (p) => (
        <div className="w-32">
          <span className="whitespace-nowrap text-sm tabular-nums text-ink">{money(p.spent)}</span>
          <ProgressBar value={p.budgeted ? p.spent / p.budgeted : 0} className="mt-1" />
        </div>
      ),
    },
    { key: "status", header: "Status", cell: (p) => <EntityStatusPill status={p.status} /> },
  ];

  return (
    <div className="space-y-3">
      {/* Partial failure — the payoff of Promise.allSettled: show what loaded, flag what didn't. */}
      {failedCount > 0 && projects.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-warn/30 bg-warnSoft px-3 py-2 text-sm text-warn">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Couldn't load projects for {failedCount} deal{failedCount === 1 ? "" : "s"} — showing the rest.
          </span>
          <Button variant="ghost" onClick={onRetry}>Retry</Button>
        </div>
      )}
      <DataTable
        columns={columns}
        rows={projects}
        rowKey={(p) => `${p.workspaceId}:${p.id}`}
        onRowClick={(p) => navigate(`/workspace/${p.workspaceId}/projects/${p.id}`)}
        empty={<EmptyState title={emptyTitle} body={emptyBody} />}
      />
    </div>
  );
}
